/**
 * SWYFT Admin — fetch helpers for passenger (uc_api) and driver APIs.
 * Bases: `REACT_APP_PASSENGER_API_URL`, `REACT_APP_DRIVER_API_URL` (full URLs, no trailing slash).
 */

function trimBase(v) {
    return (v ?? "").trim().replace(/\/$/, "");
}

export function readBases() {
    return {
        passengerBase: trimBase(process.env.REACT_APP_PASSENGER_API_URL),
        driverBase: trimBase(process.env.REACT_APP_DRIVER_API_URL),
        googleMapsApiKey: trimBase(process.env.REACT_APP_GOOGLE_MAPS_API_KEY),
    };
}

/** Chromium blocks `fetch` to localhost:6000 (uc_api default); fail early with a clear message. */
function blockedLocalPortMessage(urlStr) {
    if (!urlStr || typeof urlStr !== "string") return null;
    const t = urlStr.trim();
    if (!t || t.startsWith("/")) return null;
    try {
        const u = new URL(t.includes("://") ? t : `http://${t}`);
        const port = u.port ? parseInt(u.port, 10) : u.protocol === "https:" ? 443 : 80;
        const h = (u.hostname || "").toLowerCase();
        const local = h === "localhost" || h === "127.0.0.1" || h === "[::1]";
        if (local && port === 6000) {
            return (
                `Port ${port} is blocked by the browser (ERR_UNSAFE_PORT). ` +
                `Change uc_api PORT (e.g. 6001) and this URL, or use a deployed HTTPS origin.`
            );
        }
    } catch {
        return "Invalid API URL — use a full URL, e.g. http://localhost:6001";
    }
    return null;
}

/** Join `path` (e.g. `/admin/rides?limit=200`) to API origin. Never returns a relative URL — empty base would hit the admin dev server by mistake. */
function absoluteApiUrl(base, path) {
    const b = trimBase(base);
    if (!b || !/^https?:\/\//i.test(b)) {
        throw new Error(
            "API base URL must be a full http(s) URL (e.g. http://localhost:6001). Set REACT_APP_PASSENGER_API_URL or REACT_APP_DRIVER_API_URL and restart `npm start` after editing .env.",
        );
    }
    const p = path.startsWith("/") ? path : `/${path}`;
    try {
        return new URL(p, b).href;
    } catch {
        throw new Error(`Invalid API URL: base="${b}" path="${p}"`);
    }
}

export function asAdminList(data) {
    return Array.isArray(data) ? data : [];
}

export function getAdminConfigWarning() {
    const { passengerBase, driverBase } = readBases();
    if (!passengerBase) {
        return "Set REACT_APP_PASSENGER_API_URL in .env (uc_api base URL, no trailing slash).";
    }
    if (!driverBase) {
        return "Set REACT_APP_DRIVER_API_URL in .env (uc_drivers_api base URL, no trailing slash).";
    }
    const badP = blockedLocalPortMessage(passengerBase);
    if (badP) return `Passenger API: ${badP}`;
    const badD = blockedLocalPortMessage(driverBase);
    if (badD) return `Driver API: ${badD}`;
    return null;
}

async function backendFetch(url, options = {}, label) {
    const unsafe = blockedLocalPortMessage(url);
    if (unsafe) {
        const err = new Error(`${label}: ${unsafe}`);
        err.code = "ERR_UNSAFE_PORT";
        throw err;
    }

    const headers = new Headers(options.headers || {});
    if (!headers.has("Content-Type") && options.body && typeof options.body === "string") {
        headers.set("Content-Type", "application/json");
    }

    let res;
    try {
        res = await fetch(url, { ...options, headers, credentials: "omit" });
    } catch (e) {
        const msg = e?.message || String(e);
        const hint =
            msg.includes("Failed to fetch") || msg.includes("NetworkError")
                ? ` Cannot reach ${label} at ${url}. Check the server is running, URL in .env, and CORS (ALLOWED_CORS_ORIGINS includes this admin origin).`
                : ` ${msg}`;
        throw new Error(`${label} request failed.${hint}`);
    }

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    } catch {
        data = text;
    }
    if (!res.ok) {
        const err = new Error(
            typeof data === "object" && data?.error ? data.error : `${label}: ${res.status} ${res.statusText || "Request failed"}`,
        );
        err.status = res.status;
        err.body = data;
        throw err;
    }
    return data;
}

export async function passengerAdmin(path, options = {}) {
    const { passengerBase } = readBases();
    return backendFetch(absoluteApiUrl(passengerBase, path), options, "Passenger API (uc_api)");
}

export async function driverAdmin(path, options = {}) {
    const { driverBase } = readBases();
    return backendFetch(absoluteApiUrl(driverBase, path), options, "Driver API (uc_drivers_api)");
}

/** If a detail dialog is open for `mutatedId`, replace its payload after a successful mutation. */
export async function reloadOpenDetailIf({ dialogOpen, shownId, mutatedId, fetchDetail, setDetail }) {
    if (dialogOpen && shownId === mutatedId) {
        setDetail(await fetchDetail(mutatedId));
    }
}
